import React, { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppDispatch } from '../../app/hooks';
import Link from 'next/link';
import { useAuth } from '../../context/AuthUserContext';
import { display } from '../../data/display';
import { updateReview } from '../../lib/apis/review';
import {
  getFieldErrors,
  getErrorMsg,
  getDateStr,
  getFormData,
  clearObj,
} from '../../lib/utils';
import { updateVenueReview } from '../../features/venue/venueSlice';
import { updateServiceProviderReview } from '../../features/serviceProvider/serviceProviderSlice';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Icon from '../atoms/Icon';
import Checkbox from '../atoms/Checkbox';
import Logo from '../atoms/Logo';
import Button from '../atoms/Button';
import Rating from '../atoms/Rating';
import StepNavi from '../molecules/StepNavi';
import Feedback from '../molecules/Feedback';
import ContentBox from '../molecules/ContentBox';
import Form from '../molecules/Form';
import Input from '../atoms/Input';
import styles from '../../styles/components/organisms/ItemReviewForm.module.scss';
import Offset from './Offset';

interface ItemReviewReplyFormProps {
  data: any;
  type: string;
  onComplete: () => void;
}

const ItemReviewReplyForm = (props: ItemReviewReplyFormProps) => {
  const { t } = useTranslation(['common', 'pages', 'rating', 'request']);
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(0);
  const steps = ['intro', 'resume'];
  const { data, type, onComplete } = props;
  const planData = display[type]['platinum'] as any;
  const defaultRatings = planData.ratings || {};
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [formData, setFormData] = useState({}) as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [ratings, setRatings] = useState({ ...defaultRatings }) as any;
  const { id, contact_name, contact_type, review, rating } = data;
  const { terms, role, description } = formData;
  const itemType = t(`rating:${type}`);

  /**
   * Disable next button in step navi depending on input
   */
  const currStep = steps[step];
  const nextDisabled =
    !!error || (currStep === 'intro' && (!description || !terms));

  /**
   * Handle form input change
   */
  const handleChange = (obj: any) => {
    setFormData({ ...formData, ...obj });
    setError(false);
  };

  /**
   * Handle form errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    switch (errorMsg) {
      default:
        setError(t(`common:form-error`) + ` (${errorMsg})`);
        break;
    }
  };

  /**
   * Handle input of mandatory checkboxes
   */
  const handleCheckbox = (obj: any) => {
    const key = Object.keys(obj)[0];
    const add = obj[key] ? [] : [key];
    const arr = [...fieldErrors.filter((el: string) => el !== key), ...add];
    setFormData({ ...formData, ...obj });
    setFieldErrors(arr);
    if (!!arr.length) {
      setError(t('common:form-input-error'));
    } else {
      setError('');
    }
  };

  /**
   * Set rating state for specific category
   */
  const rate = (obj: any, count: number) => {
    const currRatings = { ...ratings };
    const currRating = currRatings[role][obj.parent][obj.id];
    currRatings[role][obj.parent][obj.id] =
      currRating === 1 && count === 1 ? 0 : count;
    setRatings(currRatings);
  };

  /**
   * Submit form data state collected during steps
   */
  const submit = async (formData: any) => {
    if (!!fieldErrors.length) {
      setError(t(`common:form-input-error`));
      return;
    }

    setError(false);
    setLoading(true);

    const payload = { reply: formData.description };

    try {
      const review = await updateReview(id, payload);
      if (!!review?.id) {
        type === 'venue'
          ? dispatch(updateVenueReview(review))
          : dispatch(updateServiceProviderReview(review));
      } else {
        handleError(review);
      }
      // await updateReview(id, payload);
      setComplete(true);
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle step navigation
   */
  const proceed = () => {
    const fieldErrors = getFieldErrors(currStep);
    setFieldErrors(fieldErrors);
    if (!!fieldErrors.length) {
      setError(t('common:form-input-error'));
    } else {
      const currData = getFormData(currStep);
      const mergedData = clearObj({ ...formData, ...currData });
      setFormData(mergedData);
      setError('');
      switch (currStep) {
        case 'resume':
          submit(mergedData);
          break;
        default:
          setLoading(false);
          setStep(step + 1);
          break;
      }
    }
  };

  return (
    <Container className={styles.itemReviewForm}>
      <Row>
        <Col xs={8} sm={6} className={'offset-sm-1'}>
          <h1>
            {complete
              ? t(`rating:reply:complete-h1`, { itemType })
              : t(`rating:reply:headline`, { itemType })}{' '}
          </h1>
          <Rating
            small
            dark
            value={rating.toFixed(1)}
            label={
              <>
                <b>{`${contact_name}, `}</b>
                {`${t(`rating:${contact_type.toLowerCase()}`)}: "${review}"`}
              </>
              // <span>
              //   <b>{`${contact_name}, `}</b>
              //   <span>
              //     {`${t(`rating:${contact_type.toLowerCase()}`)}: "${review}"`}
              //   </span>
              // </span>
            }
          />
        </Col>
      </Row>
      {complete ? (
        <>
          <Row>
            <Col xs={8} sm={6} className={'offset-sm-1'}>
              <br />
              {/* <Col xs={8} sm={6} className={'offset-sm-1'}> */}
              <h2>{t('rating:reply:complete-h2', { itemType })}</h2>
              {/* <p>{t('rating:rating-sent', { itemName: data.name })}</p> */}{' '}
              <br />
            </Col>
            <Col xs={8}>
              <Logo noLink full center />
              <br />
            </Col>
          </Row>
          <Row className={'justify-content-center'}>
            <Col xs={4} sm={2}>
              <Button
                white
                large
                full
                icon="send"
                label={t('common:cta-subscribe')}
                href={t(`pages:newsletter:link`)}
                onClick={onComplete}
              />
            </Col>
            <Col xs={4} sm={2}>
              <Button
                dark
                large
                full
                icon="close"
                label={t('common:cta-close')}
                onClick={onComplete}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Form
          checkOnce
          id={currStep}
          loading={loading}
          onFieldError={(arr) => {
            setFieldErrors(arr);
          }}
          onChange={handleChange}
        >
          {/* Feedback description */}
          {currStep === 'intro' && (
            <>
              <Row>
                <Col xs={8} sm={6} className={'offset-sm-1'}>
                  <br />
                  <br />
                  <h2>
                    <Icon name="eye" />
                    <span>
                      {t(`rating:reply:description`, { itemType })}
                      {' *'}
                    </span>
                  </h2>
                  <Offset>
                    {['description'].map((key, i) => (
                      <Input
                        mandatory
                        full
                        float
                        key={i}
                        name={key}
                        icon={key}
                        type={'textarea'}
                        label={t(`common:textarea_placeholder`)}
                        // placeholder={`${t(`common:textarea_placeholder`)} ${
                        //   !overallRating && '*'
                        // }`}
                        error={fieldErrors.indexOf(key) > -1}
                        defaultValue={formData[key] || ''}
                      />
                    ))}
                  </Offset>
                  <br />
                  <Checkbox
                    grey
                    className={styles.checkbox}
                    name="terms"
                    error={fieldErrors.indexOf('terms') > -1}
                    defaultValue={!!formData['terms']}
                    onChange={handleCheckbox}
                    label={
                      <>
                        {t('common:accept-terms')}{' '}
                        <Link href="/terms-conditions">
                          <a target="_blank" rel="noopener,noreferrer">
                            {t('pages:terms-conditions:navi')}
                          </a>
                        </Link>
                        *
                      </>
                    }
                  />
                </Col>
              </Row>
            </>
          )}

          <Row>
            {!!error && (
              <Col xs={8} sm={6} className={'offset-sm-1'}>
                <br />
                <p className={styles.error}>{error}</p>
              </Col>
            )}
          </Row>
        </Form>
      )}

      {/* Prepare for submit */}
      {currStep === 'resume' && !complete && (
        <Row>
          <Col xs={8} sm={6} className={'offset-sm-1'}>
            <h2>{t(`rating:reply:resume`)}</h2>
            <ContentBox slim autoHeight>
              <Feedback data={{ ...data, reply: formData.description }} />
            </ContentBox>
          </Col>
        </Row>
      )}

      {!complete && (
        <>
          <Row>
            <Col xs={8} sm={6} className={'offset-sm-1'}>
              <br />
              <div className={styles.footnote}>
                <span>*</span>
                <span>{t(`rating:form:mandatory-fields`)}</span>
              </div>
              <div className={styles.footnote}>
                <Icon name="eye" />
                <span>{t(`rating:form:info-visible`)}</span>
              </div>
              <br />
              <br />
              <br />
            </Col>
          </Row>

          {/**
           * Step Navigation
           * */}
          <Row className={`${styles.naviRow} justify-content-center`}>
            <Col xs={7} md={3} xl={2}>
              <StepNavi
                intro={false}
                steps={steps}
                disabled={loading || nextDisabled}
                nextDisabled={nextDisabled}
                className={styles.stepNavi}
                endLabel={t(`common:cta-submit`)}
                onNext={proceed}
                onBack={(num) => {
                  setLoading(false);
                  setStep(num);
                }}
                onComplete={proceed}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default ItemReviewReplyForm;
